import { useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Badge,
} from "@mui/material";
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  ShoppingCartOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { NotificationCenter } from "./NotificationCenter";
import { Can } from "../contexts/PermissionContext";
import { Permissions } from "../constants/permissions";
import { AppRoutes } from "../constants/appRoutes";
import { Global } from "../constants/global";

export const TopBar = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const { cartItems } = useCart();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    handleClose();
    await logout();
    navigate("/login", { replace: true });
  };

  const formatRole = (roleCode) => {
    if (!roleCode) return "Enterprise User";
    return roleCode
      .replace(Global.B2B, "")
      .replace(/([A-Z])/g, " $1")
      .trim();
  };

  const userInitials =
    `${user?.firstName?.[0] || ""}${user?.lastName?.[0] || ""}`.toUpperCase() ||
    "U";

  const cartItemCount = cartItems?.length || 0;

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: "background.paper",
        color: "text.primary",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Toolbar sx={{ px: { xs: 2, md: 3 } }}>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={onMenuClick}
          sx={{ mr: 2, display: { xs: "block", md: "none" } }}
        >
          <MenuIcon />
        </IconButton>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            component="img"
            src="/logo.svg"
            alt="Logo"
            sx={{ height: 32, display: { xs: "none", md: "block" } }}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              fontWeight: 800,
              color: "primary.main",
              letterSpacing: "-0.5px",
            }}
          >
            JIGENPASS <span style={{ color: "#64748b" }}>B2B</span>
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Can permission={Permissions.B2B_ORDER_MANAGE}>
            <IconButton
              color="inherit"
              onClick={() => navigate(AppRoutes.CHECKOUT)}
            >
              <Badge badgeContent={cartItemCount} color="primary">
                <ShoppingCartOutlined />
              </Badge>
            </IconButton>
          </Can>

          <NotificationCenter />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              ml: 1,
              p: 0.5,
              pr: 2,
              borderRadius: 50,
              cursor: "pointer",
              transition: "background-color 0.2s",
              "&:hover": { bgcolor: "action.hover" },
            }}
            onClick={handleMenu}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: "primary.main",
                fontWeight: 600,
              }}
            >
              {userInitials}
            </Avatar>
            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, lineHeight: 1.2 }}
              >
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 500 }}
              >
                {formatRole(user?.role)}
              </Typography>
            </Box>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            onClick={handleClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: "visible",
                filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.1))",
                mt: 1.5,
                minWidth: 200,
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {user?.email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Company ID: {user?.companyId || "N/A"}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={() => {
                handleClose();
                navigate(AppRoutes.PROFILE);
              }}
            >
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              Profile & Settings
            </MenuItem>

            <Divider />
            <MenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
              <ListItemIcon>
                <Logout color="error" fontSize="small" />
              </ListItemIcon>
              Sign Out
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};
